import { useCallback, useEffect, useState } from 'react';
import { ArrowRight } from 'react-bootstrap-icons';
import Table from 'react-bootstrap/esm/Table';
import Plot from 'react-plotly.js';
import { useDispatch, useSelector } from 'react-redux';
import { renderDateNumber } from '../../app/dates';
import { AppDispatch, RootState } from '../../app/store';
import ValueResponseDisplay from '../inputs/ValueResponseDisplay';
import { useAuth } from '../signin/auth';
import { formatEntriesForPlot, formatEntriesForTable, FormattedTableEntry } from '../timeseries/timeseriesSlice';
import styles from './DisplayGoalDetail.module.css';
import { getGoal, selectEntries, selectGoal } from './goalSlice';

interface DisplayGoalDetailProps {
  goalID: string
}

function DisplayGoalDetail(props: DisplayGoalDetailProps) {
  const auth = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const { goalID } = props;
  const goal = useSelector((state: RootState) => selectGoal(state));
  const entries = useSelector((state: RootState) => selectEntries(state));
  const goalsStatus = useSelector((state: RootState) => state.goal.status)
  const goalsError = useSelector((state: RootState) => state.goal.error)
  const [tableRows, setTableRows] = useState<Array<FormattedTableEntry>>([]);
  const formatGoalEntriesForPlot = useCallback(formatEntriesForPlot, []);

  useEffect(() => {
    if (auth.authToken) {
      dispatch(getGoal({
        authToken: auth.authToken,
        goalID: goalID,
        includeEntries: true,
      }));
    }
  }, [dispatch, auth.authToken, goalID]);

  useEffect(() => {
    if (entries.series.length > 0) {
      setTableRows(formatEntriesForTable(entries));
    } else if (tableRows.length !== 0) {
      setTableRows([]);
    }
  }, [setTableRows, entries, tableRows.length]);

  return (
    <>
      {goalsStatus === 'loading' && <div className="loader">Loading...</div>}
      {goalsError && <div>{goalsError}</div>}
      <div>
        <Plot
          data={formatGoalEntriesForPlot(entries)}
          layout={{
            autosize: true,
            title: goal?.name || '',
            showlegend: true,
            legend: { 'orientation': 'h' },
            hovermode: 'x unified',
            margin: { b: 50, l: 50, r: 0, t: 50 },
          }}
          useResizeHandler={true}
          style={{ width: '100%' }}
        />
      </div>
      {goal && (
        <Table striped className="mt-3">
          <tbody>
            <tr>
              <th>ID</th>
              <td><code>{goal.id}</code></td>
            </tr>
            <tr>
              <th>Name</th>
              <td>{goal.name}</td>
            </tr>
            <tr>
              <th>Prompt</th>
              <td>{goal.prompt}</td>
            </tr>
            <tr>
              <th>Dates</th>
              <td>
                {renderDateNumber(goal.startDate)}
                &nbsp;<ArrowRight />&nbsp;
                {renderDateNumber(goal.endDate)}
              </td>
            </tr>
          </tbody>
        </Table>
      )}
      {entries && (
        <>
          <h2>Progress</h2>
          <Table striped bordered size="sm" hover className={`mt-3 ${styles.entries}`}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Value</th>
                <th>Target</th>
                <th>Difference</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => {
                return (<tr key={row.key}>
                  <td>{row.date.toLocaleDateString()}</td>
                  <td><ValueResponseDisplay value={row.value} /></td>
                  <td><ValueResponseDisplay value={row.targetValue} /></td>
                  <td>{row.difference}</td>
                </tr>);
              })}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
}

export default DisplayGoalDetail;